import GemecoQuickOrder from "@gemeco/Assets/Icons/quick_order_white";
import GemecoSupport from "@gemeco/Assets/Icons/support_white";
import GemecoSupportWrench from "@gemeco/Assets/Icons/support_white_wrench";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    title = "title",
    destination = "destination",
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const link = getLink(state, ownProps.fields.destination);
    return {
        url: link?.url,
        title: link?.title,
    };
};

const mapDispatchToProps = {};

export interface HeaderLinkStyles {
    routerLink?: ClickablePresentationProps;
    iconStyle?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
}

export const headerLinkStyles: HeaderLinkStyles = {
    routerLink: {
        css: css`
            display: inline-block;
            text-align: center;
        `,
    },
    iconStyle: {
        color: "secondary.contrast",
        css: css`
            display: block;
            margin: auto;
        `,
    },
    titleTypography: {
        css: css`
            white-space: nowrap;
            display: block;
            font-weight: 500;
        `,
        ellipsis: true,
        transform: "none",
        color: "secondary.contrast",
    },
};

const iconMap = {
    gemecoQuickOrder: GemecoQuickOrder,
    gemecoSupport: GemecoSupport,
    gemecoSupportWrench: GemecoSupportWrench,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
        [fields.title]: string;
        [fields.destination]: LinkFieldValue;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = headerLinkStyles;
const HeaderLink: FC<Props> = ({ url, fields }) => {
    const showIcon = fields.visibilityState !== "label";
    const showLabel = fields.visibilityState !== "icon";
    const icon = iconMap[(showIcon && fields.icon) || "none"];
    const size = 30;

    const { routerLink } = styles;

    return (
        <Clickable className="headerLink" {...routerLink} href={url} data-test-selector="HeaderLink">
            {showIcon && <Icon {...styles.iconStyle} src={icon} size={size} />}
            {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
            {showLabel && <Typography {...styles.titleTypography}>{`${translate(fields.title)}`}</Typography>}
        </Clickable>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(HeaderLink),
    definition: {
        displayName: "Header Link",
        icon: "Link",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
                isRequired: true,
            },
            {
                name: fields.icon,
                displayName: "Display Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Gemeco Support",
                        value: "gemecoSupport",
                    },
                    {
                        displayName: "Gemeco Support Wrench",
                        value: "gemecoSupportWrench",
                    },
                    {
                        displayName: "Gemeco Quick Order",
                        value: "gemecoQuickOrder",
                    },
                ],
                defaultValue: "gemecoQuickOrder",
                fieldType: "General",
                sortOrder: 2,
                isRequired: true,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
